import { useEffect, useState } from 'react';
import {Box, Button, Stack, Typography} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AsyncAutoComplete from '../../Inputs/ResourceMapping/AsyncAutocomplete';
import LoaderComponent from 'components/Loader';
import { ReactComponent as DuplicateIcon } from '../../../assets/fad_duplicate.svg';
import { getProgramProjectHelper } from './ResourceMappingHelper';
import { copyProjectToProgram } from 'service/service';
import { ReactComponent as WarningImage } from '../../../assets/delete-img-modal.svg';
import eventEmitter from 'SnackBarEventEmitter';

interface Option {
	value: string;
	label: string;
}

interface CopyToProgramModalProps {
	projectData: any;
	onCancel: () => void;
	onConfirm: (data: { projectName: string; programName: string }) => void;
	onTitleChange: (title: string) => void;
	onWidthChange: (width: 'sm' | 'md' | 'lg' | 'xl') => void;
}

const CopyToProgramModal = ({
	projectData,
	onCancel,
	onConfirm,
	onTitleChange,
	onWidthChange
}: CopyToProgramModalProps) => {
	const theme = useTheme();
	const colors: any = theme.palette;
	const [isLoading, setIsLoading] = useState(false);
	const [showCopyInput, setShowCopyInput] = useState(true);
	const [showConflictModal, setShowConflictModal] = useState(false);
	const [selectedProgram, setSelectedProgram] = useState('');
	const [selectedProgramId, setSelectedProgramId] = useState<number>();
	const [programOptions, setProgramOptions] = useState<Option[]>([]);
	const [allProgramOptions, setAllProgramOptions] = useState<Option[]>([]);
	const duplicate_message = 'This project name is already present in this program';

	const getProgramProjectOptions = async (): Promise<Option[]> =>{
		const result = await getProgramProjectHelper('', 'program');
		const filteredPrograms = result.filter((option: Option) =>
			Number(option.value) !== Number(projectData?.programId));
		setAllProgramOptions(filteredPrograms);
		setProgramOptions(filteredPrograms);
		return filteredPrograms;
	}

	const handleSave = async (isForceUpdate = false) => {
		try {
			setIsLoading(true);
			const payload = {
				projectId: projectData?.projectId,
				targetProgramId: selectedProgramId,
				targetProgramName: selectedProgram,
				overrideExisting: isForceUpdate
			};

			const result = await copyProjectToProgram(payload);
			if (result?.success) {
				onConfirm({ projectName: projectData?.projectName || '', programName: selectedProgram });
			} else if(!result?.success && result?.response?.data?.data?.message === duplicate_message) {
				setShowCopyInput(false);
				setShowConflictModal(true);

				onTitleChange('Create Copy of Project Confirmation');
				onWidthChange('md');
			}
		} catch (err) {
			console.error(err);
			if (typeof err === 'object' && err !== null && 'response' in err) {
				const error = err as { response: { status: number } };
				if (error.response.status === 503)
					eventEmitter.emit('showSnackbar', 'Service temporarily unavailable. Please try again later.', 'info');
				else if (error.response.status === 504) eventEmitter.emit('showSnackbar', 'Gateway Timeout', 'info');
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleForceUpdate = async () => {
		await handleSave(true);
	};

	const handleProgramChange = async (value: string | null) => {
		const selectedOption = allProgramOptions.find(
			(option) => option.label === value
		);

		setSelectedProgram(value || '');
		setSelectedProgramId(
			selectedOption ? Number(selectedOption.value) : undefined
		);
	};

	useEffect(() => {
		getProgramProjectOptions();
	}, []);

	if (isLoading) {
		return (
			<LoaderComponent p = {2} bgColor={`${colors.modal.bg}`} />
		);
	}

	// CONFLICT MODAL
	if (showConflictModal) {
		return (
			<Stack
				spacing={2}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					px: 2,
					pb: 2,
					pt: 2.5,
					backgroundColor: `${colors.modal.bg} !important`,
					color: `${colors.modal.text} !important`
				}}
			>
				<Stack direction="row" alignItems="center" spacing={1}>
					<WarningImage className="warning-image" style={{ width: '4.5rem', height: '4.5rem' }} />
					<Stack
						sx={{
							alignItems: 'flex-start',
							justifyContent: 'center',
							pl: '1rem'
						}}
					>
						<Typography variant="pageTitle" sx={{ textAlign: 'left', pb: '0.5rem' }}>
							Found same project under{' '}
							<span style={{ fontWeight: 700 }}>
								{selectedProgram}
							</span>
						</Typography>
						<Typography sx={{ textAlign: 'left'}}>
							Looks like, project{' '}
							<Box component="span" sx={{ color: 'info.main', cursor: 'pointer', textDecoration: 'underline' }}>
								{projectData?.projectName}
							</Box>{' '}
							already available in{' '}
							<Box component="span" sx={{ color: 'info.main', cursor: 'pointer', textDecoration: 'underline' }}>
								{selectedProgram}
							</Box>
							.
						</Typography>
						<Typography sx={{ textAlign: 'left'}}>
							Are you sure to continue with the force update?
						</Typography>
					</Stack>
				</Stack>
				<Stack direction="row" justifyContent="space-between">
					<Button
						variant="pepwiseSecondaryOutline2"
						sx={{ color: 'info.main', width: '12rem'}}
						onClick={() => {
							setShowConflictModal(false);
							setShowCopyInput(true);

							onTitleChange('Create Copy of Project To Program');
							onWidthChange('lg');
						}}
					>
						CHANGE PROGRAM
					</Button>
					<Stack direction="row" spacing={1}>
						<Button variant="pepwiseOutline" sx={{ width: '10rem', color: 'error.main' }} onClick={onCancel}>
							CANCEL
						</Button>
						<Button
							variant="contained"
							color="error"
							sx={{
								color: 'white',
								backgroundColor: 'error.main !important',
								width: '10rem',
								borderRadius: '4px !important'
							}}
							onClick={handleForceUpdate}
						>
							FORCE UPDATE
						</Button>
					</Stack>
				</Stack>
			</Stack>
		);
	}

	// COPY INPUT SCREEN
	if (showCopyInput) {
		return (
			<Stack
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					px: 2,
					pb: 4,
					pt: 1,
					backgroundColor: `${colors.modal.bg} !important`,
					color: `${colors.modal.text} !important`
				}}
			>
				<Stack direction="row" alignItems="center" spacing={1}>
					<DuplicateIcon style={{width: '98px', height: '98px'}}/>
					<Stack
						sx={{
							alignItems: 'flex-start',
							justifyContent: 'center',
							pl: '1rem'
						}}
					>
						<Typography variant='h6' sx={{ textAlign: 'left', pb: '0.5rem', fontWeight: 400 }}>
							Choose programs where you want to create copy of the{' '}
							<span style={{ fontWeight: 500 }}>
								{projectData?.projectName}
							</span>.
						</Typography>
						<Typography sx={{ textAlign: 'left'}}>
							Project with all the configurations will be copied.
						</Typography>

					</Stack>
				</Stack>
				<Stack sx={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, mt: 0 }}>
					<Box sx={{ flexGrow: 1, minWidth: 0 }}>
						<AsyncAutoComplete
							size="small"
							textFieldLabel={'Select Program'}
							inputValue={selectedProgram}
							value={selectedProgram}
							onChange={async (event, value) => {
								handleProgramChange(value);
							}}
							mappingType=""
							columnName={'Program'}
							options={programOptions || []}
							sx={{
								width: '100%',
								minWidth: 0,
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: `${colors.input.border} !important`,
									border: `1px solid ${colors.input.border}`
								},
								'& .MuiAutocomplete-popupIndicator svg': {
									fill: `${colors.input.text} !important`
								},
								'& .MuiAutocomplete-popupIndicator': {
									color: `${colors.input.text} !important`
								},
								'& .MuiAutocomplete-clearIndicator': {
									color: `${colors.input.text} !important`
								},
								'& .MuiInputBase-root': {
									minHeight: 36,
									height: 36,
								},
								'& .MuiOutlinedInput-input': {
									py: 0.5,
								}
							}}
							componentsProps={{
								paper: {
									sx: {
										bgcolor: `${colors.dropdown.menuItemBackground} !important`,
										color: `${colors.dropdown.menuItemText} !important`
									}
								},
								popper: {
									sx: {
										'& .MuiAutocomplete-option': {
											bgcolor: `${colors.dropdown.menuItemBackground} !important`,
											color: `${colors.dropdown.menuItemText} !important`,
											'&.Mui-selected': {
												backgroundColor: `${colors.dropdown.menuItemSelected} !important`
											},
											'&:hover': {
												backgroundColor: `${colors.dropdown.menuItemHover} !important`,
												color: `#fff !important`
											}
										},
										'& .MuiAutocomplete-noOptions': {
											color: `${colors.dropdown.menuItemText} !important`,
											backgroundColor: `${colors.dropdown.menuItemBackground} !important`
										}
									}
								}
							}}
							customTextFieldProps={{
								InputLabelProps: {
									sx: {
										'&&': {
											color: `${colors.input.text} !important`
										}
									}
								},
								InputProps: {
									sx: {
										'&&': {
											color: `${colors.input.text} !important`
										}
									}
								}
							}}
						/>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, ml: 2 }}>
						<Button variant="pepwiseOutline" sx={{ width: '8rem', color: `${colors.buttons.primary.color} !important` }} onClick={onCancel}>
							CANCEL
						</Button>
						<Button
							disabled={
								!selectedProgramId ||
								selectedProgramId === Number(projectData?.programId) ||
								!programOptions?.some(
									(option) => Number(option.value) === selectedProgramId
								)
							}
							variant="pepwiseSecondary"
							sx={{ width: '8rem' }}
							onClick={() => handleSave(false)}
						>
							SAVE
						</Button>
					</Box>
				</Stack>
			</Stack>
		);
	}

	return null;
};

export default CopyToProgramModal;